<template>
    <!-- <Breadcrumbs main="Setting" mains="Medicine Management" title="Medicine Management" /> -->
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-item-center">
                    <h5>Medicine List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Medicine
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="medicines"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.image="{ item }">
                        <img v-if="item.image" :src="item.image" alt="Medicine Image" style="max-width: 60px; max-height: 60px;" />
                        <span v-else>-</span>
                    </template>
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning me-2" @click="editMedicine(item as Medicine)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteMedicine(item as Medicine)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="itemModal" tabindex="-1" ref="modalRef" aria-labelledby="itemModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Unit</label>
                                <input v-model="form.unit" type="text" class="form-control">
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Price</label>
                                <input v-model.number="form.price" type="number" class="form-control">
                            </div>
                            <div class="mb-2">
                                <label class="form-label col-form-label">Description</label>
                                <textarea v-model="form.description" class="form-control"></textarea>
                            </div>
                            <div class="mb-2">
                                <label class="form-label col-form-label">Image</label>
                                <input type="file" class="form-control" accept="image/*" @change="handleImageUpload">
                                <div v-if="form.image" class="mt-2">
                                    <img :src="form.image" alt="Preview" style="max-width: 200px; max-height: 200px;">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveMedicine">Save</button>
                    </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

// {{ Define Medicine interface }}
interface Medicine {
    ID: any; // Allow null for new medicines
    code: string;
    name: string;
    unit: string;
    price: number;
    description: string;
    image: string; // base64 string
}

const headers = [
    { text: '#', value: 'image' },
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Price', value: 'price' },
    { text: 'Unit', value: 'unit' },
    { text: 'Actions', value: 'actions' }
]

// {{ Apply types to refs and reactive objects }}
const medicines = ref<Medicine[]>([])
const loading = ref(false)
const total = ref(0)
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const modalTitle = ref('')
const form = reactive<Medicine>({
    ID: null,
    code: '',
    name: '',
    unit: '',
    price: 0,
    description: '',
    image: ''
})

// Add this data data array
const dataMedicines: Medicine[] = []
import * as master from "@/api/services/MasterRequest";

const fetchMedicine = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const resp = await master.postRequest("/medicine/list", {...params});
        dataMedicines.splice(0, dataMedicines.length);
        dataMedicines.push(...resp.data);

        // Filter based on search
        let filteredMedicines = [...dataMedicines]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredMedicines = dataMedicines.filter(medicine =>
                medicine.code.toLowerCase().includes(searchLower) ||
                medicine.name.toLowerCase().includes(searchLower)
            )
        }

        // Calculate pagination
        const start = (params.page - 1) * params.size
        const end = start + params.size
        medicines.value = filteredMedicines.slice(start, end)
        total.value = filteredMedicines.length

    } catch (error) {
        toast.error('Failed to fetch medicines')
    }
    loading.value = false
}

// {{ Add type annotation for newParams }}
const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchMedicine()
}

const modalRef = ref<HTMLElement | null>(null) // Type the ref
let bsModal: any | null = null // Type the bootstrap modal instance as any since Modal namespace can't be used directly

// View modal state
const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedMedicine = ref<Medicine | null>(null)

onMounted(() => {
    if (modalRef.value) { // Check if modalRef exists
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) { // Check if bsModal is initialized
        bsModal.hide()
    }
}

const saveMedicine = async () => {
    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        // await new Promise(resolve => setTimeout(resolve, 500));
        if (form.ID) {
            // Update existing medicine in data data
            await master.postRequest("/medicine/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Medicine updated successfully');
                    closeModal();
                    fetchMedicine(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to update medicine');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update medicine');
            })
            // const index = dataMedicines.findIndex(r => r.ID === form.ID);
            // if (index !== -1) {
            //     dataMedicines[index] = { ...form }; // Update with new form data
            // }
        } else {
            // Add new medicine to data data
            await master.postRequest("/medicine/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Medicine created successfully');
                    closeModal();
                    fetchMedicine(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to create medicine');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create medicine');
            })
        }
    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to save medicine');
    } finally {
        loading.value = false; // End loading state
    }
}

// {{ Define editMedicine function }}
const editMedicine = (medicine: Medicine) => {
    openModal(medicine); // Call openModal with the medicine data
}


// {{ Add type annotation for medicine parameter }}
const deleteMedicine = async (medicine: Medicine) => {
    if (!confirm('Are you sure you want to delete this medicine?')) return

    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        await master.postRequest("/medicine/delete", {ID: medicine.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Medicine deleted successfully');
                closeModal();
                fetchMedicine(); // Re-fetch to update the table view
            } else {
                toast.error('Failed to delete medicine');
            }
        })

    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to delete medicine');
    } finally {
        loading.value = false; // End loading state
    }
}

// Update openModal to ensure accounts array exists
const openModal = (medicine: Medicine | null = null) => {
    if (medicine) {
        modalTitle.value = 'Edit Medicine'
        Object.assign(form, medicine);
        // Deep copy accounts to avoid mutating dataMedicines directly
    } else {
        modalTitle.value = 'Create Medicine'
        form.ID = null
        form.code = ''
        form.name = ''
    }
    if (bsModal) {
        bsModal.show()
    }
}

// Image upload handler
const handleImageUpload = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files[0]) {
        const reader = new FileReader();
        reader.onload = (e) => {
            form.image = e.target?.result as string;
        };
        reader.readAsDataURL(target.files[0]);
    }
}
</script>